import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import IntelligentContactForm from './components/IntelligentContactForm';
import Icon from '../../components/AppIcon';

const NeuralNetworkContactCollaboration = () => {
  const [openProtocolMenuId, setOpenProtocolMenuId] = useState(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleClose = () => setOpenProtocolMenuId(null);
    if (openProtocolMenuId) {
      document.addEventListener('click', handleClose);
    }
    return () => document.removeEventListener('click', handleClose);
  }, [openProtocolMenuId]);

  const emergencyProtocols = [
    {
      id: 'critical-bug',
      name: 'Critical Bug Fix',
      description: 'Production issues requiring immediate attention',
      responseTime: '< 15 minutes',
      availability: '24/7',
      contact: 'ntlemezal35@gmail.com',
      icon: 'AlertTriangle',
      color: 'destructive'
    },
    {
      id: 'server-down',
      name: 'Server Downtime',
      description: 'Infrastructure failures and outages',
      responseTime: '< 30 minutes',
      availability: '24/7',
      contact: 'ntlemezal35@gmail.com',
      icon: 'Server',
      color: 'warning'
    },
    {
      id: 'security-breach',
      name: 'Security Incident',
      description: 'Security vulnerabilities and breaches',
      responseTime: '< 10 minutes',
      availability: '24/7',
      contact: 'ntlemezal35@gmail.com',
      icon: 'Shield',
      color: 'destructive'
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground particle-bg">
      <Helmet>
        <title>Neural Network - Contact & Collaboration | CyberDev Nexus</title>
  <meta name="description" content="Multi-dimensional communication center with real-time availability, collaboration tools, and professional network visualization. Connect with Lwando Ntlemeza for project work, mentoring, and partnerships." />
        <meta name="keywords" content="contact developer, collaboration tools, project inquiry, mentoring, professional network, availability matrix" />
      </Helmet>

      <Header />

      {/* Hero Section */}
      <section className="pt-24 pb-12 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-6">
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-brand-cyan via-brand-purple to-brand-green rounded-2xl flex items-center justify-center cyber-glow-intense">
                  <Icon name="Network" size={40} color="#ffffff" />
                </div>
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-brand-green rounded-full flex items-center justify-center animate-pulse">
                  <Icon name="Zap" size={12} color="#000000" />
                </div>
              </div>
            </div>
            <h1 className="font-orbitron font-black text-4xl md:text-6xl lg:text-7xl text-foreground mb-6">
              Neural <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-cyan to-brand-green">Network</span>
            </h1>
            <p className="text-xl md:text-2xl text-text-secondary max-w-4xl mx-auto mb-8 leading-relaxed">
              Multi-dimensional communication center featuring availability matrices, collaboration tools, 
              and professional network visualization for seamless connection and project initiation.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <div className="flex items-center space-x-2 px-4 py-2 bg-brand-green/10 rounded-lg cyber-border">
                <div className="w-2 h-2 bg-brand-green rounded-full animate-pulse"></div>
                <span className="text-sm font-mono text-brand-green">ONLINE & AVAILABLE</span>
              </div>
              <div className="flex items-center space-x-2 px-4 py-2 bg-brand-cyan/10 rounded-lg cyber-border">
                <Icon name="Clock" size={14} className="text-brand-cyan" />
                <span className="text-sm font-mono text-brand-cyan">RESPONSE TIME: &lt; 2 HOURS</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-12 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Intelligent Contact Form - Focused */}
          <div className="mb-12">
            <IntelligentContactForm />
          </div>
        </div>
      </section>

      {/* Emergency Contact Protocols */}
      <section className="pb-12 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="glass-morphism rounded-xl p-8 cyber-border">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-destructive to-warning rounded-lg flex items-center justify-center cyber-glow">
                  <Icon name="AlertTriangle" size={24} color="#ffffff" />
                </div>
              </div>
              <h2 className="font-orbitron font-bold text-2xl md:text-3xl text-foreground mb-4">
                Emergency Contact Protocols
              </h2>
              <p className="text-text-secondary max-w-2xl mx-auto">
                For existing clients with project-critical issues requiring immediate attention
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {emergencyProtocols.map((protocol) => (
                <div
                  key={protocol.id}
                  className={`p-6 rounded-lg border transition-all duration-300 cyber-hover ${
                    protocol.color === 'destructive' ?'border-destructive/30 bg-destructive/5 hover:bg-destructive/10' :'border-warning/30 bg-warning/5 hover:bg-warning/10'
                  }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      protocol.color === 'destructive' ? 'bg-destructive/20' : 'bg-warning/20'
                    }`}>
                      <Icon 
                        name={protocol.icon} 
                        size={20} 
                        className={protocol.color === 'destructive' ? 'text-destructive' : 'text-warning'} 
                      />
                    </div>
                    <div className={`px-2 py-1 rounded text-xs font-medium ${
                      protocol.color === 'destructive' ?'bg-destructive/20 text-destructive' :'bg-warning/20 text-warning'
                    }`}>
                      {protocol.availability}
                    </div>
                  </div>

                  <h3 className="font-medium text-foreground mb-2">{protocol.name}</h3>
                  <p className="text-sm text-text-secondary mb-4">{protocol.description}</p>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-text-tertiary">Response Time</span>
                      <span className={protocol.color === 'destructive' ? 'text-destructive' : 'text-warning'}>
                        {protocol.responseTime}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-text-tertiary">Contact</span>
                      <span className="text-foreground font-mono">{protocol.contact}</span>
                    </div>
                  </div>

                  <div className="relative" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => { e.stopPropagation(); setOpenProtocolMenuId(openProtocolMenuId === protocol.id ? null : protocol.id); }}
                      className={`w-full py-2 px-4 rounded-lg font-medium text-sm transition-all duration-300 cyber-hover ${
                        protocol.color === 'destructive' ?'bg-destructive/20 text-destructive hover:bg-destructive/30' :'bg-warning/20 text-warning hover:bg-warning/30'
                      }`}
                    >
                      Emergency Contact
                    </button>

                    {openProtocolMenuId === protocol.id && (
                      <div className="absolute z-10 mt-2 left-0 right-0 glass-morphism rounded-lg cyber-border p-2" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between gap-2">
                          <a
                            href={`mailto:ntlemezal35@gmail.com?subject=${encodeURIComponent('Emergency: ' + protocol.name)}&body=${encodeURIComponent('Hi Lwando,\n\nEmergency type: ' + protocol.name + '\nDetails: ')}`}
                            className="flex-1 py-2 rounded-md bg-surface/40 hover:bg-surface/60 transition-colors flex items-center justify-center"
                            title="Email"
                            aria-label="Email"
                            onClick={() => setOpenProtocolMenuId(null)}
                          >
                            <Icon name="Mail" size={18} />
                          </a>
                          <a
                            href={`tel:+27631819011`}
                            className="flex-1 py-2 rounded-md bg-surface/40 hover:bg-surface/60 transition-colors flex items-center justify-center"
                            title="Call"
                            aria-label="Call"
                            onClick={() => setOpenProtocolMenuId(null)}
                          >
                            <Icon name="Phone" size={18} />
                          </a>
                          <a
                            href={`https://wa.me/27631819011?text=${encodeURIComponent('Emergency: ' + protocol.name + ' — please assist.')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 py-2 rounded-md bg-surface/40 hover:bg-surface/60 transition-colors flex items-center justify-center"
                            title="WhatsApp"
                            aria-label="WhatsApp"
                            onClick={() => setOpenProtocolMenuId(null)}
                          >
                            <Icon name="Whatsapp" size={18} />
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 p-4 bg-surface/30 rounded-lg cyber-border text-center">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Icon name="Info" size={16} className="text-brand-cyan" />
                <span className="text-sm font-medium text-foreground">Important Notice</span>
              </div>
              <p className="text-xs text-text-secondary">
                Emergency protocols are reserved for existing clients with active projects experiencing critical issues. 
                For new inquiries, please use the standard contact form above.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="pb-24 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="glass-morphism rounded-xl p-8 cyber-border">
            <h2 className="font-orbitron font-bold text-2xl md:text-3xl text-foreground mb-4">
              Ready to Connect?
            </h2>
            <p className="text-text-secondary mb-6">
              Choose your preferred communication channel and let's start building something amazing together.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="flex items-center justify-center space-x-2 px-6 py-3 bg-brand-cyan text-black rounded-lg font-medium cyber-hover cyber-glow">
                <Icon name="MessageSquare" size={18} />
                <span>Start Conversation</span>
              </button>
              <button className="flex items-center justify-center space-x-2 px-6 py-3 bg-brand-green/10 text-brand-green rounded-lg font-medium cyber-hover cyber-border">
                <Icon name="Calendar" size={18} />
                <span>Schedule Meeting</span>
              </button>
              <button className="flex items-center justify-center space-x-2 px-6 py-3 bg-brand-purple/10 text-brand-purple rounded-lg font-medium cyber-hover cyber-border">
                <Icon name="Briefcase" size={18} />
                <span>Project Inquiry</span>
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NeuralNetworkContactCollaboration;